/**
 * Header menu diagnostics. Inert unless the URL carries ?menudebug=1.
 *
 * Logs every pointerenter on a top-level nav item together with whether the
 * <header-menu> element had upgraded by that point, and wraps activate() and
 * deactivate() so an open-then-immediately-close shows up as two lines with a
 * gap of a few milliseconds between them.
 *
 * Reading a trace:
 *   "enter ... upgraded=false"     the JS had not loaded; the hover is dropped
 *   "activate" with no "deactivate" the menu opened and stayed open
 *   "activate" then "deactivate"    it opened and something closed it - the
 *                                   caller is on the deactivate line
 *   no "enter" at all               the event never reached the item
 */
(() => {
  /* Armed by ?menudebug=1 OR a stored flag. The storefront password gate
     redirects through /password and drops the query string, so the parameter
     alone does not survive reaching the page - the flag is persisted on sight
     and read back on later loads. menuDebugOff() clears it. */
  let armed = false;
  try {
    if (/[?&]menudebug=1\b/.test(location.search)) {
      localStorage.setItem('menudebug', '1');
      armed = true;
    } else {
      armed = localStorage.getItem('menudebug') === '1';
    }
  } catch (e) {
    armed = /[?&]menudebug=1\b/.test(location.search);
  }

  window.menuDebugOff = () => {
    try { localStorage.removeItem('menudebug'); } catch (e) {}
    return 'menu diagnostics off - reload to apply';
  };

  if (!armed) {
    window.menuDebug = () =>
      'Diagnostics are off. Run: localStorage.menudebug = 1  then reload the page.';
    return;
  }

  const t0 = performance.now();
  const lines = [];
  const push = (msg) => {
    const line = `+${(performance.now() - t0).toFixed(0)}ms  ${msg}`;
    lines.push(line);
    console.log('%c[menu]', 'color:#a68c62;font-weight:700', line);
  };
  window.menuDebug = () => lines.join('\n');

  const menuEl = () => document.querySelector('header-menu');
  const upgraded = () => {
    const el = menuEl();
    return !!(el && typeof el.activate === 'function');
  };

  document.addEventListener(
    'pointerenter',
    (e) => {
      const item = e.target instanceof Element ? e.target.closest('.menu-list__list-item') : null;
      if (!item) return;
      const label = (item.textContent || '').trim().split('\n')[0].slice(0, 20);
      const overflow = item.getAttribute('slot') === 'overflow';
      /* trusted=false marks a replayed enter: the adoption/self-heal in
         header-menu.js recovering a hover whose real boundary event the
         browser never dispatched. A failing hover that recovers shows as a
         trusted=false enter with no trusted one before it. */
      push(
        `enter "${label}" upgraded=${upgraded()} overflow=${overflow} w=${innerWidth} pointer=${e.pointerType} trusted=${e.isTrusted}`
      );
    },
    true
  );

  const wrap = () => {
    const el = menuEl();
    if (!el) return push('no <header-menu> in the document');
    ['activate', 'deactivate'].forEach((name) => {
      const original = el[name];
      if (typeof original !== 'function') return push(`${name}() is not a function`);
      el[name] = function (event) {
        const target = event && event.target instanceof Element ? event.target.tagName.toLowerCase() : '-';
        const related =
          event && event.relatedTarget instanceof Element ? event.relatedTarget.tagName.toLowerCase() : '-';
        const caller = (new Error().stack || '').split('\n')[2] || '';
        push(`${name}  target=${target} related=${related} ${caller.trim().slice(0, 70)}`);
        return original.apply(this, arguments);
      };
    });
    push(`instrumented (upgraded=${upgraded()})`);
  };

  customElements.whenDefined('header-menu').then(() => requestAnimationFrame(wrap));
  setTimeout(() => { if (!upgraded()) push('still not upgraded after 4s'); }, 4000);
  push('debug armed - reproduce the fault, then run copy(menuDebug()). menuDebugOff() to stop.');
})();
